import { UploadErrorCode, UploadTaskStatus, UploadTaskPriority } from '@fabric-app/types/upload';
import { uploadStore, logUploadTaskEvent, logUploadTaskError } from '../upload.store.js';
import { getWoodyClient } from '../../woody/woody.js';
import { prepareUploadFile } from './prepareUploadFile.js';
import { UploadError } from '../upload.error.js';
import { initializeChunks } from './initializeFileChunks.js';
import { UploadProgressTracker } from '../classes/UploadProgressTracker.js';
import { uploadChunk } from './uploadChunk.js';
import { ensureError, isDefined } from '@fabric-app/utils/guards';
import { getCompatPlatform } from '@fabric-app/compat/capabilities/platform';
import { compareVersions } from 'compare-versions';

const handleUpload = async (options) => {
  const { addTask, updateTask, failTask } = uploadStore.getState();
  let taskId = void 0;
  const abortListener = () => {
    if (isDefined(taskId)) {
      failTask(taskId, new UploadError(UploadErrorCode.FileUploadCancelled));
    }
  };
  try {
    if (!options.data.available) {
      throw new UploadError(UploadErrorCode.FileNotAvailable);
    }
    const { file, fileSize } = await prepareUploadFile(options);
    const isNativeUpload = options.data.type === "uri";
    const activePlatform = getCompatPlatform().getSimplifiedPlatform();
    const activePlatformVersion = await getCompatPlatform().getPlatformVersion();
    const isNativeChunkedSupported = activePlatform === "android" && compareVersions(activePlatformVersion.appVersion ?? "0.0.0-build.0", "0.1.29-build.47") >= 0 || activePlatform === "ios" && compareVersions(activePlatformVersion.appVersion ?? "0.0.0-build.0", "0.4.10-build.1") >= 0;
    return new Promise((resolve, reject) => {
      const task = addTask({
        data: options.data.type === "uri" ? {
          ...options.data,
          type: "uri",
          file: {
            ...options.data.file,
            mimeType: file.type,
            size: fileSize
          }
        } : options.data.type === "file" ? {
          ...options.data,
          type: "file",
          file,
          available: true
        } : options.data.type === "todesktop-stream" && !!options.data.stream ? {
          ...options.data,
          type: "todesktop-stream",
          stream: options.data.stream,
          file: {
            ...options.data.file,
            name: file.name,
            mimeType: file.type,
            size: fileSize
          },
          available: true
        } : options.data,
        priority: options.priority ?? UploadTaskPriority.Low,
        maxConcurrentChunksCount: options.maxConcurrentChunksCount,
        initialize: async (_task) => {
          let task2 = _task;
          logUploadTaskEvent(task2.id, "Initializing upload task...");
          taskId = task2.id;
          task2 = updateTask(task2.id, (task3) => ({
            ...task3,
            status: UploadTaskStatus.Uploading,
            startTime: Date.now()
          }));
          const { chunks, key, uploadId, storagePath } = await initializeChunks(task2, file, fileSize, options);
          logUploadTaskEvent(task2.id, `Upload task initialized chunks. Multipart: ${task2.chunked}`);
          const progressTracker = new UploadProgressTracker(task2.id, options.onProgress);
          task2 = updateTask(task2.id, (task3) => ({
            ...task3,
            chunks: chunks.map((c) => ({
              ...c,
              status: UploadTaskStatus.Pending,
              progress: 0,
              uploadSpeed: 0,
              retries: 0,
              execute: async (task4, chunk) => {
                uploadChunk(task4.id, chunk.index, options, progressTracker);
              }
            })),
            result: {
              key,
              storagePath,
              uploadId
            }
          }));
        },
        complete: async (task2) => {
          const client = await getWoodyClient();
          if (task2.chunked) {
            logUploadTaskEvent(task2.id, "Completing multipart upload...");
            await client.v2("/v2/upload/multipart/complete", {
              method: "post",
              body: {
                parts: task2.chunks.map((c) => ({
                  partNumber: c.index + 1,
                  etag: c.etag ?? ""
                })),
                uploadId: task2.result?.uploadId ?? "",
                path: task2.result?.storagePath ?? ""
              }
            });
          }
        },
        resolve: (result) => {
          logUploadTaskEvent(task.id, "File upload completed.");
          resolve({
            key: result.key,
            file
          });
        },
        reject,
        onChunkBegin: options.onChunkBegin
      }, {
        disableChunking: isNativeUpload && !isNativeChunkedSupported
      });
      taskId = task.id;
      options.abortSignal?.addEventListener("abort", abortListener);
      if (options.abortSignal?.aborted) failTask(task.id, new UploadError(UploadErrorCode.FileUploadCancelled));
      logUploadTaskEvent(task.id, "Upload task created.");
    });
  } catch (e) {
    const error = e instanceof UploadError ? e : new UploadError(UploadErrorCode.Unknown, ensureError(e).message);
    if (isDefined(taskId)) {
      logUploadTaskError(taskId, error);
      failTask(taskId, error);
    }
    throw error;
  } finally {
    options.abortSignal?.removeEventListener("abort", abortListener);
  }
};

export { handleUpload };
//# sourceMappingURL=handleUpload.js.map
